import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Handler from "../../Helpers/Handler";
import { formatDateTime } from "../../Helpers/Utils";
import ListLayout from "../../Components/List";
import CustomerRegisterModal from "./CustomerRegisterModal";
import { useAuth } from "../../Contexts/AuthenticateProvider";
import useListParams from "../../Helpers/useListParams";
import DuplicateAlerts from "./DuplicateAlerts";
import TestModeTag from "../../Components/TestModeTag";
// TEMPORARY (temp/linked-customer)
import LinkedRegisterModal from "../LinkedCustomer/LinkedRegisterModal";
import LinkedChip from "../LinkedCustomer/LinkedChip";
import { linkedApi, useLinkedEnabled } from "../LinkedCustomer/linkedApi";

const INITIAL_FILTERS = { search: "", searchKey: "name", count: 10, status: "", kyc: "", testMode: "" };
const SEARCH_KEYS = [{ key: "name", value: "Name / phone / customer ID / email" }];

const CustomerList = () => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const canRegister = hasPermission("customer.register");
    // Page number in the URL path, filters in the query string - see Helpers/useListParams.
    const { page, setPage, filters, setFilters } = useListParams(INITIAL_FILTERS);
    const [data, setData] = useState({ list: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showRegister, setShowRegister] = useState(false);
    // TEMPORARY (temp/linked-customer)
    const linkedEnabled = useLinkedEnabled();
    const [showLinkedRegister, setShowLinkedRegister] = useState(false);
    const [linkedStatus, setLinkedStatus] = useState({});

    const filterItems = {
        dropdown: [
            {
                key: "status",
                name: "Status",
                options: [
                    { key: "", name: "All" },
                    { key: "true", name: "Active" },
                    { key: "false", name: "Inactive" },
                ],
            },
            {
                key: "kyc",
                name: "KYC",
                options: [
                    { key: "", name: "All" },
                    { key: "rejected", name: "Rejected" },
                    { key: "name_mismatch", name: "Name mismatch" },
                ],
            },
            {
                // Test mode: customers whose bookings are paid on the Razorpay test account
                // (shown in the Customer ID column's filter).
                key: "testMode",
                column: "customerId",
                name: "Test mode",
                options: [
                    { key: "", name: "All" },
                    { key: "true", name: "Test mode on" },
                    { key: "false", name: "Test mode off" },
                ],
            },
        ],
    };

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const response = await Handler({
            method: "post",
            url: `/staff/customer/list/${page - 1}`,
            params: { count: filters.count },
            data: {
                ...(filters.search ? { search: filters.search } : {}),
                ...(filters.status !== "" ? { status: filters.status === "true" } : {}),
                ...(filters.kyc ? { kyc: filters.kyc } : {}),
                ...(filters.testMode !== "" && filters.testMode !== undefined ? { testMode: filters.testMode === "true" } : {}),
            },
        });
        if (response?.success) {
            setData(response?.data?.data || { list: [], total: 0 });
        } else {
            setData({ list: [], total: 0 });
            setError(response?.data?.message || "No data found");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filters.count, filters.search, filters.status, filters.kyc, filters.testMode]);

    useEffect(() => { load(); }, [load]);

    // TEMPORARY (temp/linked-customer): one call for the customers on this page.
    useEffect(() => {
        const ids = (data.list || []).map((row) => row.customerId).filter(Boolean);
        if (!linkedEnabled || !ids.length) { setLinkedStatus({}); return undefined; }
        let alive = true;
        linkedApi().status(ids.slice(0, 100)).then((r) => { if (alive) setLinkedStatus(r.ok && r.data ? r.data : {}); });
        return () => { alive = false; };
    }, [linkedEnabled, data.list]);

    const onFilterChange = (key, value) => {
        if (key !== "count") setPage(1);
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const resetFilter = () => {
        setFilters(INITIAL_FILTERS);
        setPage(1);
    };

    const columns = [
        { key: "customerId", name: "Customer ID", mandatory: true, render: (row) => <>{row.customerId}<TestModeTag mode={row.testMode === true ? "test" : "live"} /></> },
        { key: "name", name: "Name", render: (row) => [row.firstName, row.lastName].filter(Boolean).join(" ") || <span className="text-muted">-</span> },
        { key: "phone", name: "Phone" },
        { key: "email", name: "Email", render: (row) => row.email?.emailId || <span className="text-muted">-</span> },
        { key: "coins", name: "Coins", render: (row) => row.coins ?? 0 },
        {
            key: "status",
            name: "Status",
            render: (row) => <span className={`badge rounded-pill ${row.status ? "text-bg-success" : "text-bg-secondary"}`}>{row.status ? "Active" : "Inactive"}</span>,
        },
        { key: "createdAt", name: "Joined", render: (row) => formatDateTime(row.createdAt) },
    ];
    if (linkedEnabled) columns.push({ key: "bookingForm", name: "Booking Form", render: (row) => <LinkedChip entry={linkedStatus[row.customerId]} /> });

    return (
        <>
            <DuplicateAlerts basePath="/staff/customer" />
            <ListLayout
                title="Customers"
                description="Customer directory - profiles, KYC verification and account status."
                data={data}
                loading={loading}
                error={error}
                onRetry={load}
                onRefresh={load}
                table={columns}
                page={page}
                setPageNo={setPage}
                pageSize={filters.count}
                onPageSizeChange={(size) => onFilterChange("count", size)}
                filters={filters}
                initialFilters={INITIAL_FILTERS}
                filterItems={filterItems}
                onFilterChange={onFilterChange}
                resetFilter={resetFilter}
                searchKeys={SEARCH_KEYS}
                searchPlaceholder="Search by name, phone, customer ID, or email..."
                onRowClick={(row) => navigate(`/customers/${row.customerId}`)}
                addList={canRegister ? () => setShowRegister(true) : undefined}
                toolbarExtra={linkedEnabled && canRegister ? (
                    <button type="button" className="btn btn-outline-primary" onClick={() => setShowLinkedRegister(true)}>Linked Customer Registration</button>
                ) : undefined}
            />

            {showLinkedRegister ? (
                <LinkedRegisterModal
                    onClose={() => { setShowLinkedRegister(false); load(); }}
                    onOpenCustomer={(customerId) => { setShowLinkedRegister(false); navigate(`/customers/${customerId}`); }}
                />
            ) : ""}

            {showRegister ? (
                <CustomerRegisterModal
                    onClose={() => setShowRegister(false)}
                    onCreated={(customerId) => { setShowRegister(false); navigate(`/customers/${customerId}`); }}
                />
            ) : ""}
        </>
    );
};

export default CustomerList;
